import { FC, useEffect, useState } from "react"
import { IDocumentCreatorProps } from "../../../@types/documents"
import { LoadingStatus } from "../../../@types/enumsGlobal"
import { DefaultGrid } from "../../../components/grids/default/defaultGrid"
import { PluginWrapper } from "../../../components/plugins"
import { ILotMovementDTO, ILotMovementViewDTO } from "../../../libs/coreapi-dto/accounting/lotMovement"
import useGridFilter from "../../../system/hooks/useGridFilter"
import { useAppContext } from "../../../system/providers/appContextProvider"
import { v4 as uuidv4 } from "uuid";
import { LotMovementGridPluginSettings } from "."
import { LotMovementDataProvider } from "../../../Services/DataProviders/LotMovementDataProvider"
import style from "./styles/LotMovement.module.scss";
import FlexRow from "../../../components/controls/FlexRow"
import { LotMovementPanel } from "./Components/LotMovementPanel"
import SearchModalWindowAntd from "../../../components/modalWindows/SearchModalWindowAntd/SearchModalWindowAntd"
import { DateTime } from "luxon"
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker"
import { TreeGrid } from "../../../components/grids/treeGrid/treeGrid"

type ILotMovementTreeRow = ILotMovementViewDTO & {
    idRow?: string;
    idLotGlobal?: string;
    idParentLotGlobal?: string;
    dateOp?: string | Date | DateTime;
    idDocument?: string;
    typeOp?: string;
};

const DateOpFilterFormat = "yyyyMMdd";

const createDefaultDatePeriod = (): IDatePeriod => ({
    startDate: DateTime.now().minus({ days: 14 }).startOf("day"),
    endDate: DateTime.now().endOf("day"),
});

const createDateOpFilters = (datePeriod: IDatePeriod): IGridColumnFilter[] => [
    ...(datePeriod.startDate
        ? [{
            name: "dateOp",
            value: datePeriod.startDate.startOf("day").toFormat(DateOpFilterFormat),
            operator: "MoreOrEq",
        } as IGridColumnFilter]
        : []),
    ...(datePeriod.endDate
        ? [{
            name: "dateOp",
            value: datePeriod.endDate.plus({ days: 1 }).startOf("day").toFormat(DateOpFilterFormat),
            operator: "Less",
        } as IGridColumnFilter]
        : []),
];



const LotMovementCreatorView: FC<IDocumentCreatorProps<ILotMovementDTO>> = (props) => {
    const appContext = useAppContext()
    const lotMovementDataProvider = new LotMovementDataProvider(appContext.coreApiService)

    const [idLotGlobal,] = useState<string | undefined>(props.idGlobal)
    const [idGoodsGlobal] = useState<string | undefined>(props.idGoodsGlobal)

    const [data, setData] = useState<ILotMovementViewDTO[]>([]);
    const [dataVersion, setDataVersion] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Completed);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(createDefaultDatePeriod);
    const defaultGridFilter: IGridFilter = {
        pageNumber: 1,
        numberPerPage: 10000,
        columnFilters: createDateOpFilters(datePeriod),
        paramFilter: { idLot: idLotGlobal, idGoods: idGoodsGlobal }
    }
    
    const [gridFilter, dispatchGridFilter] = useGridFilter(defaultGridFilter);

    useEffect(() => {
        fetchData();
    }, [gridFilter, dataVersion])

    const fetchData = async () => {
        try {
            setLoadingStatus(LoadingStatus.InProcess);
            lotMovementDataProvider.getView(gridFilter, (data, totalCount) => {
                data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
                setData(data);
                setTotalCount(totalCount);
            });
        } catch {
            setLoadingStatus(LoadingStatus.Failed);
        }
    };
    
    const getStyleRow = (row: IGridRow): string | null => {
        let cell = row.cells.find(c => c.propertyName === 'typeOp');
        if (!cell) return null
        if (cell.value === 'Резерв' || cell.value === 'Оперативный резерв') {
            return style.td
        }
        return null
    };

    const getDateOpTime = (dateOp: ILotMovementTreeRow["dateOp"]) => {
        if (DateTime.isDateTime(dateOp)) return dateOp.toMillis();

        const time = new Date(dateOp ?? "").getTime();
        return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
    };

    const changeTreeData = (items: ILotMovementTreeRow[]) => {
        const groupsByLot = new Map<string, ILotMovementTreeRow[]>();
        const childGroupsByParentAndLot = new Map<string, ILotMovementTreeRow[]>();
        const parentLotIds = new Set<string>();

        items.forEach(item => {
            if (item.idParentLotGlobal) {
                parentLotIds.add(item.idParentLotGlobal);
            }

            if (!item.idLotGlobal) return;

            const group = groupsByLot.get(item.idLotGlobal) ?? [];
            group.push(item);
            groupsByLot.set(item.idLotGlobal, group);

            if (item.idParentLotGlobal && item.idLotGlobal !== item.idParentLotGlobal) {
                const childGroupKey = `${item.idParentLotGlobal}_${item.idLotGlobal}`;
                const childGroup = childGroupsByParentAndLot.get(childGroupKey) ?? [];
                childGroup.push(item);
                childGroupsByParentAndLot.set(childGroupKey, childGroup);
            }
        });

        const mainRowsByLot = new Map<string, ILotMovementTreeRow>();
        const mainRowsByParentAndLot = new Map<string, ILotMovementTreeRow>();

        groupsByLot.forEach((group, idLotGlobal) => {
            if (group.length <= 1) return;

            const mainRow = group.reduce((earliest, item) =>
                getDateOpTime(item.dateOp) < getDateOpTime(earliest.dateOp) ? item : earliest
            );
            mainRowsByLot.set(idLotGlobal, mainRow);
        });

        childGroupsByParentAndLot.forEach((group, childGroupKey) => {
            if (group.length <= 1) return;

            const mainRow = group.reduce((earliest, item) =>
                getDateOpTime(item.dateOp) < getDateOpTime(earliest.dateOp) ? item : earliest
            );
            mainRowsByParentAndLot.set(childGroupKey, mainRow);
        });

        const treeIdsByRow = new Map<ILotMovementTreeRow, string | undefined>();

        items.forEach(item => {
            const mainRow = item.idLotGlobal ? mainRowsByLot.get(item.idLotGlobal) : undefined;
            const isDuplicate = mainRow !== undefined && mainRow !== item;
            treeIdsByRow.set(item, isDuplicate ? item.idRow ?? item.idGlobal : item.idLotGlobal);
        });

        const treeItems = items.map(item => {
            const mainRowByLot = item.idLotGlobal ? mainRowsByLot.get(item.idLotGlobal) : undefined;
            const childGroupKey = item.idParentLotGlobal && item.idLotGlobal
                ? `${item.idParentLotGlobal}_${item.idLotGlobal}`
                : undefined;
            const mainRowByParentAndLot = childGroupKey ? mainRowsByParentAndLot.get(childGroupKey) : undefined;
            const isChildDuplicate = mainRowByParentAndLot !== undefined && mainRowByParentAndLot !== item;
            const isLotDuplicate = mainRowByLot !== undefined && mainRowByLot !== item;
            const shouldMoveLotDuplicateToMain = isLotDuplicate && item.idLotGlobal && (
                parentLotIds.has(item.idLotGlobal) || !item.idParentLotGlobal
            );
            const treeParentLotGlobal = isChildDuplicate
                ? treeIdsByRow.get(mainRowByParentAndLot)
                : shouldMoveLotDuplicateToMain
                    ? treeIdsByRow.get(mainRowByLot)
                    : item.idParentLotGlobal;

            return {
                ...item,
                treeIdLotGlobal: treeIdsByRow.get(item),
                treeIdParentLotGlobal: treeParentLotGlobal,
            };
        });

        const treeItemIds = new Set(treeItems.map(item => item.treeIdLotGlobal).filter(Boolean));
        const visibleTreeItems = treeItems.map(item => ({
            ...item,
            treeIdParentLotGlobal: item.treeIdParentLotGlobal && treeItemIds.has(item.treeIdParentLotGlobal)
                ? item.treeIdParentLotGlobal
                : undefined,
        }));

        return visibleTreeItems.map((item, index) => ({ item, index }))
            .sort((a, b) => {
                const isSameHierarchyLevel = a.item.treeIdParentLotGlobal === b.item.treeIdParentLotGlobal;
                const isSameDocument = a.item.idDocument && a.item.idDocument === b.item.idDocument;

                if (isSameHierarchyLevel && isSameDocument) {
                    const typeOpOrder = (typeOp?: string) => {
                        if (typeOp === "Расход") return 0;
                        if (typeOp === "Приход") return 1;
                        return 2;
                    };

                    const typeCompare = typeOpOrder(a.item.typeOp) - typeOpOrder(b.item.typeOp);
                    if (typeCompare !== 0) return typeCompare;
                }

                return a.index - b.index;
            })
            .map(({ item }) => item);
    };

    const hasActiveFilters = () => {
        if (!gridFilter?.columnFilters) return false;
    
        return gridFilter.columnFilters.some(filter =>
            filter.name !== "deleted"
        );
    };

    const handleSearchToggle = () => {
        setIsSearchModalVisible(!isSearchModalVisible);
    };

    const handleRefresh = () => {
        setDataVersion(prev => prev + 1);
    };

    const handleSearch = (columnFilters: IGridColumnFilter[]) => {
        dispatchGridFilter({ type: 'search', payload: columnFilters });
        handleSearchToggle();
        setDataVersion(prev => prev + 1);
    }

    const handleDatePeriodChange = (date: IDatePeriod) => {
        setDatePeriod(date);
        dispatchGridFilter({
            type: "sortByFilters",
            payload: {
                gridColumnFilter: createDateOpFilters(date),
            },
        });
    };

    return <>
        <PluginWrapper>
            <FlexRow>
                <LotMovementPanel
                    datePeriod={datePeriod}
                    dispatchGridFilter={dispatchGridFilter}
                    hasActiveFilters={hasActiveFilters()}
                    onDatePeriodChange={handleDatePeriodChange}
                    onClickOnSearchButton={handleSearchToggle}
                    onClickRefresh={handleRefresh}
                />
            </FlexRow>
            <TreeGrid
                showQuantity
                gridId={uuidv4()}
                data={data}
                loadingStatus={loadingStatus}
                setLoadingStatus={setLoadingStatus}
                totalCount={totalCount}
                plugin={LotMovementGridPluginSettings}
                selectedItem={selectedItem}
                idParent="treeIdLotGlobal"
                idChildren="treeIdParentLotGlobal"
                changeData={changeTreeData}
                getStyleRow={(row) => getStyleRow(row)}
                onSelect={(row) => {
                    setSelectedItem(row)
                }}
                onDoubleClick={(i) => { }}
            />
            {isSearchModalVisible && (
                <SearchModalWindowAntd
                columns={LotMovementGridPluginSettings.columns ?? []}
                defaultFilters={gridFilter?.columnFilters}
                search={handleSearch}
                cancel={handleSearchToggle}
                open={true}
                onCancel={handleSearchToggle}
                />
            )}
        </PluginWrapper>
    </>
}

export default LotMovementCreatorView
